<template>
    <div>
        <div class="main-container">
            <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

            <!-- Back nav -->
            <div class="req-back">
                <a class="req-back__link" href="javascript:void(0)"
                    @click="$router.push({ path: '/kids-device', query: { profileId: profileId, serialNumber: serialNumber } })">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Home
                </a>
            </div>

            <!-- Page header -->
            <div class="gf-page-header">
                <div>
                    <h1 class="gf-page-header__title">Location Alerts</h1>
                    <p class="gf-page-header__sub">Set safe zones and get notified on entry or exit</p>
                </div>
                <button class="gf-create-btn" :disabled="isDisabled" @click="createGeofence()">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                    New
                </button>
            </div>

            <!-- Loader -->
            <div class="gf-loader" v-if="isLoading">
                <i class="fa fa-spinner fa-spin"></i>
                <span>Loading geofences...</span>
            </div>

            <template v-else>
                <!-- Empty state -->
                <div class="gf-empty" v-if="geofences.length === 0">
                    <div class="gf-empty__icon">
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="#d1d5db" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <circle cx="12" cy="10" r="3" stroke="#d1d5db" stroke-width="1.5"/>
                        </svg>
                    </div>
                    <span class="gf-empty__title">No geofences yet</span>
                    <span class="gf-empty__sub">Create a safe zone to get location alerts</span>
                    <button class="gf-create-btn gf-create-btn--full" @click="createGeofence()">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                        Create Your First Geofence
                    </button>
                </div>

                <!-- Geofence list -->
                <div class="gf-list" v-else>
                    <div class="gf-card" v-for="geofence in geofences" :key="geofence._id"
                        :class="{ 'gf-card--inactive': !geofence.isActive }">

                        <!-- Card header -->
                        <div class="gf-card__header">
                            <div class="gf-card__icon" :style="{ background: geofence.color }">
                                <i :class="getIconClass(geofence.icon)" style="color:#fff; font-size:16px;"></i>
                            </div>
                            <div class="gf-card__info">
                                <span class="gf-card__name">{{ geofence.name }}</span>
                                <div class="gf-card__meta">
                                    <span class="gf-badge gf-badge--type">{{ geofence.icon }}</span>
                                    <span class="gf-status" :class="geofence.isActive ? 'gf-status--on' : 'gf-status--off'">
                                        <span class="gf-status__dot"></span>
                                        {{ geofence.isActive ? 'Active' : 'Inactive' }}
                                    </span>
                                </div>
                            </div>
                            <label class="kid-action-switch" @click.stop>
                                <input type="checkbox"
                                    :checked="geofence.isActive"
                                    @change="toggleStatus(geofence)"
                                    :disabled="geofence.isToggling"
                                />
                                <span class="kid-action-switch__track"></span>
                            </label>
                        </div>

                        <!-- Location -->
                        <div class="gf-card__section">
                            <div class="gf-section__label">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" stroke-width="1.8"/>
                                    <circle cx="12" cy="10" r="3" stroke="currentColor" stroke-width="1.8"/>
                                </svg>
                                Location
                            </div>
                            <span class="gf-section__address">{{ geofence.location.address }}</span>
                            <span class="gf-section__coords">{{ geofence.location.latitude.toFixed(5) }}, {{ geofence.location.longitude.toFixed(5) }}</span>
                        </div>

                        <!-- Details strip -->
                        <div class="gf-details-strip">
                            <!-- Radius -->
                            <div class="gf-detail">
                                <div class="gf-detail__icon gf-detail__icon--blue">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"/>
                                        <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.8"/>
                                    </svg>
                                </div>
                                <div class="gf-detail__body">
                                    <span class="gf-detail__val">{{ geofence.radius }}m</span>
                                    <span class="gf-detail__label">Radius</span>
                                </div>
                            </div>

                            <!-- Notifications -->
                            <div class="gf-detail">
                                <div class="gf-detail__icon gf-detail__icon--purple">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                        <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                    </svg>
                                </div>
                                <div class="gf-detail__body">
                                    <span class="gf-detail__val">
                                        <span v-if="geofence.notifyOnEntry && geofence.notifyOnExit">Both</span>
                                        <span v-else-if="geofence.notifyOnEntry">Entry</span>
                                        <span v-else-if="geofence.notifyOnExit">Exit</span>
                                        <span v-else>None</span>
                                    </span>
                                    <span class="gf-detail__label">Alerts</span>
                                </div>
                            </div>

                            <!-- Last event -->
                            <div class="gf-detail" v-if="geofence.stats && geofence.stats.lastEventType">
                                <div class="gf-detail__icon"
                                    :class="geofence.stats.lastEventType === 'entry' ? 'gf-detail__icon--green' : 'gf-detail__icon--red'">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                        <path v-if="geofence.stats.lastEventType === 'entry'" d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path v-else d="M19 12H5M11 18l-6-6 6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </div>
                                <div class="gf-detail__body">
                                    <span class="gf-detail__val">{{ geofence.stats.lastEventType === 'entry' ? 'Entered' : 'Left' }}</span>
                                    <span class="gf-detail__label">{{ formatTime(geofence.stats.lastEventTime) }}</span>
                                </div>
                            </div>

                            <!-- Total events -->
                            <div class="gf-detail" v-if="geofence.stats">
                                <div class="gf-detail__icon gf-detail__icon--orange">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                                        <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                    </svg>
                                </div>
                                <div class="gf-detail__body">
                                    <span class="gf-detail__val">{{ geofence.stats.totalEvents || 0 }}</span>
                                    <span class="gf-detail__label">Events</span>
                                </div>
                            </div>
                        </div>

                        <!-- Footer actions -->
                        <div class="gf-card__footer">
                            <button class="gf-action-btn gf-action-btn--history"
                                v-if="geofence.stats"
                                @click="viewGeofenceHistory(geofence)">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                                    <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                </svg>
                                History
                            </button>
                            <button class="gf-action-btn gf-action-btn--edit"
                                @click="editGeofence(geofence)"
                                :disabled="geofence.isDeleting">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                Edit
                            </button>
                            <button class="gf-action-btn gf-action-btn--delete"
                                @click="confirmDelete(geofence)"
                                :disabled="geofence.isDeleting">
                                <i v-if="geofence.isDeleting" class="fa fa-spinner fa-spin"></i>
                                <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none">
                                    <polyline points="3 6 5 6 21 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                                </svg>
                                {{ geofence.isDeleting ? 'Deleting...' : 'Delete' }}
                            </button>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Delete confirm modal -->
            <div class="gs-modal-overlay" v-if="showDeleteModal" @click.self="cancelDelete()">
                <div class="gs-modal">
                    <div class="gs-modal__title">Delete Geofence?</div>
                    <div class="gs-modal__body">
                        Are you sure you want to delete <strong>{{ geofenceToDelete?.name }}</strong>? This action cannot be undone. History will be preserved.
                    </div>
                    <div class="gs-modal__footer">
                        <button class="gs-modal__cancel" @click="cancelDelete()" :disabled="isDeleting">Cancel</button>
                        <button class="gs-modal__confirm" @click="deleteGeofence()" :disabled="isDeleting">
                            <span v-if="isDeleting"><i class="fa fa-spinner fa-spin"></i></span>
                            <span v-else>Delete</span>
                        </button>
                    </div>
                </div>
            </div>

            <div class="last-updated" style="padding: 16px 0 24px;">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                    <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
                {{ geofences.length }} geofence{{ geofences.length !== 1 ? 's' : '' }} configured
            </div>

            <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
        </div>
    </div>
</template>

<script>
import GeofenceService from "@/services/GeofenceService";
import moment from "moment";
import KidHeader from "@/components/KidHeader.vue";
import KidFooter from "@/components/KidFooter.vue";

export default {
    name: "GeofenceList",
    components: { KidHeader, KidFooter },
    data() {
        return {
            profileId: this.$route.query.profileId || "",
            serialNumber: this.$route.query.serialNumber || "",
            geofences: [],
            isLoading: false,
            isDisabled: false,
            showDeleteModal: false,
            geofenceToDelete: null,
            isDeleting: false
        };
    },
    computed: {
        activeCount() { return this.geofences.filter(g => g.isActive).length; },
        totalEvents() { return this.geofences.reduce((sum, g) => sum + (g.stats?.totalEvents || 0), 0); }
    },
    mounted() { this.loadGeofences(); },
    methods: {
        async loadGeofences() {
            this.isLoading = true;
            try {
                const response = await GeofenceService.getGeofences(this.profileId, this.serialNumber);
                if (response.data.success) {
                    this.geofences = (response.data.data || []).map(g => ({ ...g, isToggling: false, isDeleting: false }));
                }
            } catch (error) {
                this.$toast.error("Failed to load geofences");
            } finally {
                this.isLoading = false;
            }
        },
        async toggleStatus(geofence) {
            if (geofence.isToggling) return;
            geofence.isToggling = true;
            const prev = geofence.isActive;
            geofence.isActive = !geofence.isActive;
            try {
                const response = await GeofenceService.toggleGeofenceStatus(geofence._id, { profileId: this.profileId, serialNumber: this.serialNumber });
                if (response.data.success) {
                    //this.$toast.success(`Geofence ${geofence.isActive ? 'activated' : 'deactivated'}`);
                } else {
                    geofence.isActive = prev;
                    this.$toast.error("Failed to update status");
                }
            } catch {
                geofence.isActive = prev;
                this.$toast.error("Failed to update status");
            } finally {
                geofence.isToggling = false;
            }
        },
        createGeofence() {
            this.$router.push({ path: "/geofence/create", query: { profileId: this.profileId, serialNumber: this.serialNumber } });
        },
        editGeofence(geofence) {
            this.$router.push({ path: "/geofence/create", query: { profileId: this.profileId, serialNumber: this.serialNumber, geofenceId: geofence._id, edit: true } });
        },
        viewGeofenceHistory(geofence) {
            this.$router.push({ path: "/geofence/history", query: { profileId: this.profileId, serialNumber: this.serialNumber, geofenceId: geofence._id, geofenceName: geofence.name } });
        },
        confirmDelete(geofence) { this.geofenceToDelete = geofence; this.showDeleteModal = true; },
        cancelDelete() { this.showDeleteModal = false; this.geofenceToDelete = null; },
        async deleteGeofence() {
            if (!this.geofenceToDelete) return;
            this.isDeleting = true;
            try {
                const response = await GeofenceService.deleteGeofence(this.geofenceToDelete._id, { profileId: this.profileId, serialNumber: this.serialNumber });
                if (response.data.success) {
                    //this.$toast.success("Geofence deleted");
                    this.geofences = this.geofences.filter(g => g._id !== this.geofenceToDelete._id);
                    this.cancelDelete();
                } else {
                    this.$toast.error("Failed to delete");
                }
            } catch {
                this.$toast.error("Failed to delete");
            } finally {
                this.isDeleting = false;
            }
        },
        getIconClass(icon) {
            const map = { home: "fas fa-home", school: "fas fa-school", hospital: "fas fa-hospital", store: "fas fa-shopping-cart", friend: "fas fa-user-friends", family: "fas fa-users", other: "fas fa-map-marker-alt" };
            return map[icon] || map.other;
        },
        formatTime(time) { if (!time) return ""; return moment(time).fromNow(); }
    }
};
</script>

<style scoped>
/* ── Back nav ── */
.req-back { padding: 12px 14px 8px; width: 100%; text-align: left; }
.req-back__link {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 13px; font-weight: 600; color: #374151;
    text-decoration: none; -webkit-tap-highlight-color: transparent;
}

/* ── Page header ── */
.gf-page-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 4px 14px 14px; width: 100%; text-align: left;
}
.gf-page-header__title { font-size: 20px; font-weight: 700; color: #111; margin: 0 0 2px; }
.gf-page-header__sub { font-size: 12px; color: #6b7280; margin: 0; }

/* ── Create button ── */
.gf-create-btn {
    display: inline-flex; align-items: center; gap: 5px;
    height: 36px; padding: 0 14px;
    background: #2563eb; color: #fff;
    border: none; border-radius: 10px;
    font-size: 13px; font-weight: 600;
    cursor: pointer; flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
}
.gf-create-btn:active { opacity: 0.85; }
.gf-create-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.gf-create-btn--full {
    width: 100%; justify-content: center;
    height: 44px; border-radius: 12px; margin-top: 16px;
}

/* ── Loader ── */
.gf-loader {
    display: flex; flex-direction: column; align-items: center;
    gap: 10px; padding: 48px 14px;
    color: #6b7280; font-size: 13px;
}

/* ── Empty ── */
.gf-empty {
    display: flex; flex-direction: column; align-items: center;
    gap: 6px; padding: 48px 14px; text-align: center;
    width: 100%;
}
.gf-empty__icon {
    width: 72px; height: 72px; border-radius: 50%;
    background: #f3f4f6;
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 8px;
}
.gf-empty__title { font-size: 16px; font-weight: 700; color: #111; }
.gf-empty__sub { font-size: 13px; color: #6b7280; }

/* ── List ── */
.gf-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 14px;
    width: 100%;
}

/* ── Card ── */
.gf-card {
    background: #fff;
    border-radius: 14px;
    border: 0.5px solid #e5e7eb;
    overflow: hidden;
    transition: opacity 0.15s;
    text-align: left;
}
.gf-card--inactive { opacity: 0.65; }
.gf-card--inactive:hover { opacity: 1; }

/* ── Card header ── */
.gf-card__header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 12px 10px;
    border-bottom: 0.5px solid #f3f4f6;
}
.gf-card__icon {
    width: 40px; height: 40px; border-radius: 11px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
}
.gf-card__info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.gf-card__name {
    font-size: 14px;
    font-weight: 700;
    color: #111;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.gf-card__meta {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.gf-badge {
    font-size: 10px; font-weight: 600;
    padding: 2px 8px; border-radius: 20px;
    text-transform: capitalize;
}
.gf-badge--type { background: #dcfce7; color: #15803d; }

.gf-status {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 11px; font-weight: 600;
}
.gf-status__dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: currentColor; display: inline-block;
}
.gf-status--on { color: #16a34a; }
.gf-status--off { color: #9ca3af; }

/* ── Switch ── */
.kid-action-switch {
    position: relative; display: inline-flex;
    width: 38px; height: 20px; cursor: pointer; margin: 0; flex-shrink: 0;
}
.kid-action-switch input { opacity: 0; width: 0; height: 0; position: absolute; }
.kid-action-switch__track {
    position: absolute; inset: 0; background: #d1d5db;
    border-radius: 999px; transition: background 0.2s;
}
.kid-action-switch__track::after {
    content: ''; position: absolute;
    top: 3px; left: 3px; width: 14px; height: 14px;
    background: white; border-radius: 50%; transition: transform 0.2s;
}
.kid-action-switch input:checked + .kid-action-switch__track { background: #2563eb; }
.kid-action-switch input:checked + .kid-action-switch__track::after { transform: translateX(18px); }

/* ── Section ── */
.gf-card__section {
    padding: 12px 14px;
    border-bottom: 0.5px solid #f9fafb;
    display: flex; flex-direction: column; gap: 3px;
}
.gf-section__label {
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 10px; font-weight: 700; color: #9ca3af;
    text-transform: uppercase; letter-spacing: 0.5px;
    margin-bottom: 4px;
}
.gf-section__address {
    font-size: 13px;
    font-weight: 500;
    color: #111;
    line-height: 1.4;
    word-break: break-word;
}

.gf-section__coords {
    font-size: 11px;
    color: #9ca3af;
    font-family: monospace;
    word-break: break-all;
}

/* ── Details strip ── */
.gf-details-strip {
    display: flex;
    padding: 12px 14px;
    gap: 0;
    border-bottom: 0.5px solid #f3f4f6;
    flex-wrap: wrap;
}
.gf-details-strip::-webkit-scrollbar { display: none; }

.gf-detail {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    padding: 6px 10px;
    border-right: 0.5px solid #f3f4f6;
    /* 2 per row on mobile, 4 on desktop */
    flex: 1 1 45%;
}
@media (min-width: 400px) {
    .gf-detail {
        flex: 1 1 0;
    }
}
.gf-detail:first-child { padding-left: 0; }
.gf-detail:last-child { border-right: none; }

/* Remove border on wrapped second row */
.gf-detail:nth-child(2) { border-right: none; }
.gf-detail:nth-child(3) { padding-left: 0; border-right: 0.5px solid #f3f4f6; }

.gf-detail__icon {
    width: 28px; height: 28px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
}
.gf-detail__icon--blue { background: #dbeafe; color: #1d4ed8; }
.gf-detail__icon--purple { background: #ede9fe; color: #7c3aed; }
.gf-detail__icon--green { background: #dcfce7; color: #16a34a; }
.gf-detail__icon--red { background: #fee2e2; color: #dc2626; }
.gf-detail__icon--orange { background: #ffedd5; color: #ea580c; }

.gf-detail__body { display: flex; flex-direction: column; gap: 1px; }
.gf-detail__val { font-size: 12px; font-weight: 700; color: #111; line-height: 1; }
.gf-detail__label { font-size: 10px; color: #9ca3af; font-weight: 500; }

/* ── Footer actions ── */
.gf-card__footer {
    display: flex;
    gap: 8px;
    padding: 10px 14px;
    background: #f9fafb;
    flex-wrap: nowrap;
}
.gf-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 36px;
    padding: 0 8px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    flex: 1;
    justify-content: center;
    border: 0.5px solid #e5e7eb;
    white-space: nowrap;
    min-width: 0;
    overflow: hidden;
    -webkit-tap-highlight-color: transparent;
    transition: opacity 0.12s;
}
.gf-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.gf-action-btn:active:not(:disabled) { opacity: 0.75; }

.gf-action-btn--history { background: #f3f4f6; color: #374151; }
.gf-action-btn--edit { background: #eff6ff; color: #2563eb; border-color: #bfdbfe; }
.gf-action-btn--delete { background: #fff5f5; color: #dc2626; border-color: #fecaca; }

/* ── Delete modal ── */
.gs-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.45);
    z-index: 300;
    display: flex;
    align-items: flex-end;
    justify-content: center;
}
.gs-modal {
    background: #fff;
    border-radius: 20px 20px 0 0;
    width: 100%;
    max-width: 575px;
    padding: 20px 16px 32px;
    animation: slideUp 0.25s ease;
}
@media (max-width: 575px) {
    .gs-modal {
        padding-bottom: calc(60px + env(safe-area-inset-bottom, 16px));
    }
}
@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
.gs-modal__title { font-size: 17px; font-weight: 700; color: #111; margin-bottom: 8px; }
.gs-modal__body { font-size: 13px; color: #6b7280; line-height: 1.5; margin-bottom: 20px; }
.gs-modal__footer { display: flex; gap: 10px; }
.gs-modal__cancel {
    flex: 1; height: 44px; border-radius: 12px;
    border: 0.5px solid #e5e7eb; background: #f3f4f6;
    font-size: 14px; font-weight: 600; color: #374151; cursor: pointer;
}
.gs-modal__confirm {
    flex: 1; height: 44px; border-radius: 12px; border: none;
    background: #dc2626; color: #fff;
    font-size: 14px; font-weight: 600; cursor: pointer;
}
.gs-modal__confirm:disabled, .gs-modal__cancel:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Last updated ── */
.last-updated {
    display: flex; align-items: center; justify-content: center;
    gap: 5px; font-size: 12px; color: #9ca3af;
}

button:focus { outline: none; }
</style>